import { FC } from 'react';
import { message } from 'antd';
import { PlusOutlined, MinusOutlined } from '@ant-design/icons';
import { observer } from 'mobx-react';
import { useDesktop, useLayout, useProduct } from '@/store';
import { $fm, $t } from '@/Intl';

interface LayoutBoxRightProps {
  screen: number;
  onSetScreen: (n: number) => void;
  handleAddDir: () => void;
}

// 交换屏幕
const swapScreen = (target: Array<any>, prev: number, next: number) => {
  let prevItems: typeof target = [];
  let nextItems: typeof target = [];
  let restItems: typeof target = [];
  target.forEach((item) => {
    if (item.screen === prev) {
      nextItems.push({ ...item, screen: next });
    } else if (item.screen === next) {
      prevItems.push({ ...item, screen: prev });
    } else {
      restItems.push(item);
    }
  });

  return [...prevItems, ...nextItems, ...restItems];
};

// plusscreen
const plusScreen = (target: Array<any>, screen: number) => {
  let afterItems: typeof target = [];
  let restItems: typeof target = [];
  target.forEach((item) => {
    if (item.screen >= screen) {
      afterItems.push({ ...item, screen: item.screen + 1 });
    } else {
      restItems.push(item);
    }
  });
  return [...afterItems, ...restItems];
};

const minusScreen = (target: Array<any>, screen: number) => {
  let afterItems: typeof target = [];
  let restItems: typeof target = [];
  target.forEach((item) => {
    if (item.screen > screen) {
      afterItems.push({ ...item, screen: item.screen - 1 });
    } else {
      restItems.push(item);
    }
  });
  return [...afterItems, ...restItems];
};

// 右侧工具栏
const LayoutBoxRight: FC<LayoutBoxRightProps> = observer(
  ({ screen = 0, onSetScreen, handleAddDir }) => {
    const [desktop, updateDesktop] = useDesktop();
    const [layout, updateLayout] = useLayout();
    const [product] = useProduct();

    const handleSetHome = () => {
      updateDesktop({
        homeIndex: screen,
      });
    };

    // 向上移动屏幕
    const handleMoveUp = () => {
      if (screen === 0) return false;
      const { sider, folder, widget, app } = layout;
      updateLayout({
        sider,
        folder: swapScreen(folder!, screen - 1, screen),
        widget: swapScreen(widget!, screen - 1, screen),
        app: swapScreen(app!, screen - 1, screen),
      });
      if (screen === desktop.homeIndex)
        updateDesktop({ homeIndex: screen - 1 });
      if (screen - 1 === desktop.homeIndex)
        updateDesktop({ homeIndex: screen });
      onSetScreen(screen - 1);
    };

    // 向下移动屏幕
    const handleMoveDown = () => {
      if (screen === desktop.desktopNum! - 1) return false;
      const { sider, folder, widget, app } = layout;
      updateLayout({
        sider,
        folder: swapScreen(folder!, screen + 1, screen),
        widget: swapScreen(widget!, screen + 1, screen),
        app: swapScreen(app!, screen + 1, screen),
      });
      if (screen === desktop.homeIndex)
        updateDesktop({ homeIndex: screen + 1 });
      if (screen + 1 === desktop.homeIndex)
        updateDesktop({ homeIndex: screen });
      onSetScreen(screen + 1);
    };

    const handlePlus = () => {
      if (desktop.desktopNum! >= 8) {
        return message.error($t('ADD_PAGE_WARN'));
      }
      const { sider, folder, widget, app } = layout;
      updateLayout({
        sider,
        folder: plusScreen(folder!, screen),
        widget: plusScreen(widget!, screen),
        app: plusScreen(app!, screen),
      });
      updateDesktop({
        desktopNum: desktop.desktopNum! + 1,
        homeIndex:
          desktop.homeIndex! > screen
            ? desktop.homeIndex! + 1
            : desktop.homeIndex!,
      });
      onSetScreen(Math.min(screen + 1, 7));
    };

    const handleMinus = () => {
      if (desktop.desktopNum! <= 1) {
        return message.error($t('DEL_PAGE_WARN2'));
      }
      const { sider, folder, widget, app } = layout;
      if (
        folder?.find((f) => f.screen === screen) ||
        widget?.find((w) => w.screen === screen) ||
        app?.find((a) => a.screen === screen)
      ) {
        return message.error($t('DEL_PAGE_WARN'));
      }
      updateLayout({
        sider,
        folder: minusScreen(folder!, screen),
        widget: minusScreen(widget!, screen),
        app: minusScreen(app!, screen),
      });
      updateDesktop({
        desktopNum: desktop.desktopNum! - 1,
        homeIndex:
          desktop.homeIndex! > screen
            ? desktop.homeIndex! - 1
            : desktop.homeIndex!,
      });
      onSetScreen(Math.max(screen - 1, 0));
    };

    return (
      <div className='layout-box-right'>
        <div className='layout-box-sider-top'>
          <span className='set-home' onClick={handleSetHome}></span>
          <span className='move-up' onClick={handleMoveUp}></span>
          <span className='move-down' onClick={handleMoveDown}></span>
        </div>
        <div className='layout-box-sider-middle'>
          {Array(desktop.desktopNum || 4)
            .fill('')
            .map((_, i) => {
              return (
                <span
                  key={i}
                  onClick={() => onSetScreen(i)}
                  className={`screen-btn ${i === screen ? 'active' : ''} ${
                    desktop.homeIndex === i ? 'ishome' : ''
                  }`}
                >
                  {i + 1}
                </span>
              );
            })}
        </div>
        <div className='layout-box-sider-bottom'>
          <span className='add-screen' onClick={handlePlus}>
            <PlusOutlined />
          </span>
          <span className='del-screen' onClick={handleMinus}>
            <MinusOutlined />
          </span>
          {product === 'gac2570' ? null : (
            <span className='add-dir' onClick={handleAddDir}>
              {$fm('NEW_FOLDER')}
            </span>
          )}
        </div>
      </div>
    );
  },
);

export default LayoutBoxRight;
